import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { usePermiso } from '../sesion/ProveedorDeSesion';

interface Cuenta {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly tipo: string;
  readonly padreId: string | null;
  readonly activa: boolean;
  readonly hijas: number;
}

/**
 * El plan de cuentas.
 *
 * Se muestra como **árbol y no como tabla**, que es la diferencia entre leerlo y descifrarlo: un
 * plan de cuentas se entiende por sangría —qué cuelga de qué— y una grilla plana de cuarenta filas
 * con códigos punteados obliga a reconstruir la jerarquía con la vista.
 *
 * El servidor lo manda plano, con el identificador del padre. Armar el árbol acá es barato y evita
 * que la respuesta tenga una forma que sólo le sirve a esta pantalla.
 */
export function PlanDeCuentas(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeAdministrar = usePermiso('CONTABILIDAD.PLAN.ADMINISTRAR');

  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [padreId, setPadreId] = useState('');
  const [errorDelAlta, setErrorDelAlta] = useState<string | null>(null);
  const [renombrando, setRenombrando] = useState<Cuenta | null>(null);

  const consulta = useQuery({
    queryKey: ['cuentas-contables'],
    queryFn: () => pedir<Cuenta[]>('/cuentas-contables'),
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/cuentas-contables', {
        method: 'POST',
        cuerpo: { codigo, nombre, padreId: padreId === '' ? undefined : padreId },
      }),
    onSuccess: async () => {
      setAbierto(false);
      setCodigo('');
      setNombre('');
      setPadreId('');
      setErrorDelAlta(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-contables'] });
    },
    onError: (falla: unknown) => {
      setErrorDelAlta(textoDeError(falla));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: (cuenta: Cuenta) =>
      pedir(`/cuentas-contables/${cuenta.id}`, {
        method: 'PATCH',
        cuerpo: { activa: !cuenta.activa },
      }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-contables'] });
    },
  });

  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) return <Problema error={consulta.error} />;

  const cuentas = consulta.data;

  /** Las cuentas que cuelgan de una, en el orden en que vinieron —que ya es por código—. */
  const hijasDe = (padre: string | null): Cuenta[] =>
    cuentas.filter((cuenta) => cuenta.padreId === padre);

  /**
   * Una rama y todo lo que cuelga de ella.
   *
   * La sangría es el dato: sin ella el plan es una lista de códigos y hay que leerlos dígito por
   * dígito para saber quién contiene a quién.
   */
  function rama(cuenta: Cuenta, nivel: number): React.ReactElement[] {
    const esAgrupacion = cuenta.hijas > 0;
    return [
      <li key={cuenta.id}>
        <div
          className="flex items-center gap-3 border-b border-borde py-1.5"
          style={{ paddingLeft: `${String(nivel * 1.25)}rem` }}
        >
          <span className="numero w-28 shrink-0 text-xs text-texto-tenue">{cuenta.codigo}</span>
          <span className={esAgrupacion ? 'font-semibold' : ''}>{cuenta.nombre}</span>

          {esAgrupacion ? (
            <Insignia tono="neutro">{t('contabilidad.agrupacion')}</Insignia>
          ) : (
            <Insignia tono="bien">{t('contabilidad.imputable')}</Insignia>
          )}
          {cuenta.activa ? null : <Insignia tono="mal">{t('contabilidad.inactiva')}</Insignia>}

          {puedeAdministrar ? (
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                className="rounded-marco border border-borde px-2 py-0.5 text-xs hover:bg-acento-suave"
                onClick={() => {
                  setRenombrando(cuenta);
                }}
              >
                {t('contabilidad.renombrar')}
              </button>
              <button
                type="button"
                className="rounded-marco border border-borde px-2 py-0.5 text-xs hover:bg-acento-suave"
                onClick={() => {
                  cambiarEstado.mutate(cuenta);
                }}
              >
                {cuenta.activa ? t('contabilidad.desactivar') : t('contabilidad.activar')}
              </button>
            </div>
          ) : null}
        </div>
      </li>,
      ...hijasDe(cuenta.id).flatMap((hija) => rama(hija, nivel + 1)),
    ];
  }

  return (
    <section className="flex flex-col gap-4">
      <Encabezado
        titulo={t('contabilidad.titulo')}
        descripcion={t('contabilidad.descripcion')}
        acciones={
          puedeAdministrar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
              }}
            >
              {t('contabilidad.nueva')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        {cuentas.length === 0 ? (
          <Vacio titulo={t('contabilidad.vacio')} />
        ) : (
          <ul className="text-sm">{hijasDe(null).flatMap((raiz) => rama(raiz, 0))}</ul>
        )}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('contabilidad.nueva')}
        descripcion={t('contabilidad.ayudaNueva')}
        onCerrar={() => {
          setAbierto(false);
          setErrorDelAlta(null);
        }}
        acciones={
          <Boton
            tono="principal"
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('contabilidad.codigo')}
            value={codigo}
            onChange={(evento) => {
              setCodigo(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('contabilidad.nombre')}
            value={nombre}
            onChange={(evento) => {
              setNombre(evento.target.value);
            }}
          />

          {/* De quién cuelga. El tipo no se pregunta: se hereda, y decirlo acá evita que alguien
              lo busque. */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs font-medium text-texto-tenue">
              {t('contabilidad.cuelgaDe')}
            </span>
            <select
              className="rounded-marco border border-borde bg-superficie px-3 py-2 text-sm"
              value={padreId}
              onChange={(evento) => {
                setPadreId(evento.target.value);
              }}
            >
              <option value="">{t('contabilidad.ninguna')}</option>
              {cuentas
                .filter((cuenta) => cuenta.activa)
                .map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>
                    {cuenta.codigo} · {cuenta.nombre}
                  </option>
                ))}
            </select>
            <span className="text-xs text-texto-tenue">{t('contabilidad.ayudaTipo')}</span>
          </label>

          {errorDelAlta === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {errorDelAlta}
            </p>
          )}
        </div>
      </Dialogo>

      {renombrando === null ? null : (
        <RenombrarCuenta
          key={renombrando.id}
          cuenta={renombrando}
          onCerrar={() => {
            setRenombrando(null);
          }}
        />
      )}
    </section>
  );
}

/**
 * Cambiarle el nombre a una cuenta.
 *
 * Es lo único que se corrige además de si está activa: el código, el tipo y el padre no, porque
 * reescribirían el balance de los ejercicios ya cerrados. El nombre no se congela en los asientos,
 * así que los libros de cualquier período la muestran con el nombre nuevo.
 */
function RenombrarCuenta({
  cuenta,
  onCerrar,
}: {
  readonly cuenta: Cuenta;
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [nombre, setNombre] = useState(cuenta.nombre);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () =>
      pedir(`/cuentas-contables/${cuenta.id}`, {
        method: 'PATCH',
        cuerpo: { nombre: nombre.trim() },
      }),
    onSuccess: async () => {
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-contables'] }),
        // Los conceptos de caja y su selector nombran la cuenta.
        clienteDeConsultas.invalidateQueries({ queryKey: ['conceptos-de-caja'] }),
        clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-para-la-caja'] }),
      ]);
      onCerrar();
    },
    onError: (falla: unknown) => {
      setError(textoDeError(falla));
    },
  });

  const sinCambios = nombre.trim() === '' || nombre.trim() === cuenta.nombre;

  return (
    <Dialogo
      abierto
      titulo={t('contabilidad.renombrar')}
      descripcion={t('contabilidad.ayudaRenombrar', { codigo: cuenta.codigo })}
      onCerrar={onCerrar}
      acciones={
        <Boton
          tono="principal"
          cargando={guardar.isPending}
          disabled={sinCambios}
          onClick={() => {
            guardar.mutate();
          }}
        >
          {t('acciones.guardar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-3">
        <Campo
          etiqueta={t('contabilidad.nombre')}
          obligatorio
          maxLength={120}
          value={nombre}
          onChange={(evento) => {
            setNombre(evento.target.value);
          }}
        />
        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
